/**
 * The 12-stage evaluation framework expressed as types (the "framework as data"
 * runtime lives in the service; these are the shared contracts).
 *
 * WHY here: `Idea.stages`/`verdict` are persisted (contract), and the
 * `stage.evaluated` / `verdict.reached` domain events carry `Results`/`Verdict`
 * across boundaries — so the shapes are versioned contracts. The math + registry
 * that produce them live in `services/idea-engine/src/engine` (service owns logic).
 *
 * Source of truth: the Advanced AI-Native VC framework doc (stages 1–6, 8–9,
 * 11–12, 14, 17–18). LLM supplies ASSUMPTIONS; the app computes every RESULT.
 */
import type { StageProvenance } from './provenance.js';

/** Ordered 12-stage chain (TAM → … → Final Confidence/Verdict). */
export type StageKey =
  | 'tam'
  | 'sam'
  | 'qsam'
  | 'aom'
  | 'ivs'
  | 'ram'
  | 'customerValue'
  | 'unitEconomics'
  | 'cacLtv'
  | 'winRate'
  | 'defensibility'
  | 'scenarios'
  | 'confidence';

/** Canonical evaluation order. */
export const STAGE_ORDER: readonly StageKey[] = [
  'tam',
  'sam',
  'qsam',
  'aom',
  'ivs',
  'ram',
  'customerValue',
  'unitEconomics',
  'cacLtv',
  'winRate',
  'defensibility',
  'scenarios',
  'confidence',
] as const;

// ── Per-stage Assumptions (LLM-supplied) ─────────────────────────────────────

/**
 * An inclusive low–high band around an evidence-backed value (ADR 0059). Carried for
 * display/provenance only — scoring always consumes the point value it brackets.
 */
export interface EvidenceRange {
  readonly low: number;
  readonly high: number;
}

/**
 * Where an assumption field's current value came from (ADR 0059 §4): the generating
 * model (`llm`), a manual operator edit (`manual`), or an applied evidence estimate
 * (`evidence`). Additive — cells written before this tag simply omit it.
 */
export type AssumptionProvenanceTag = 'llm' | 'manual' | 'evidence';

export interface TamAssumptions {
  readonly totalCustomers: number;
  readonly annualRevenuePerCustomer: number;
  /** Evidence range around `totalCustomers` (people channel, ADR 0059). Display-only. */
  readonly totalCustomersRange?: EvidenceRange;
  /** Evidence range around `annualRevenuePerCustomer` (ARPU channel, ADR 0059). Display-only. */
  readonly annualRevenuePerCustomerRange?: EvidenceRange;
  /** Per-field provenance tags (`llm` | `manual` | `evidence`) for the fields above. */
  readonly assumptionProvenance?: Partial<Record<string, AssumptionProvenanceTag>>;
}
export interface SamAssumptions {
  readonly serviceableCustomers: number;
}
/**
 * QSAM qualification — the chosen band LEVEL (0–4, 4 = best) for each of the five qualify
 * filters. The qualification *rate* is derived from the band via the QSAM scoring matrix
 * (`engine/qsam-matrix.ts`), so the score is computed from the matrix rather than emitted as a
 * free-hand rate (schema v7). Legacy v≤6 rows carrying `*Rate` fields self-heal to the nearest
 * band level at the parse boundary.
 */
export interface QsamAssumptions {
  readonly painLevel: number;
  readonly budgetFitLevel: number;
  readonly dataAvailabilityLevel: number;
  readonly legalEligibilityLevel: number;
  readonly digitalReadinessLevel: number;
}
/** AOM adoption rates, each in [0,1] (includes its own competitive win rate). */
/**
 * AOM adoption — the chosen band LEVEL (0–4, 4 = best) for each of the six adoption gates. The
 * pass rate is derived from the band via the AOM scoring matrix (`engine/aom-matrix.ts`), so the
 * score is computed from the matrix rather than a free-hand rate (schema v8). Legacy v≤7 rows
 * carrying `*Rate` fields self-heal to the nearest band level at the parse boundary.
 */
export interface AomAssumptions {
  readonly awarenessLevel: number;
  readonly urgencyLevel: number;
  readonly trustLevel: number;
  readonly adoptionEaseLevel: number;
  readonly competitiveWinLevel: number;
  readonly retentionLevel: number;
}
/**
 * §IVS — Intuitive Value Score factors, each scored 0–10. The consumer-comprehension gate:
 * whether a target consumer understands the problem, benefit, and reason to act in seconds.
 */
export interface IvsAssumptions {
  readonly problemRecognition: number;
  readonly outcomeClarity: number;
  readonly explanationSimplicity: number;
  readonly behaviorFit: number;
  readonly urgencyDesire: number;
  readonly trustSimplicity: number;
}
/** RAM risk/feasibility factors, each in [0,1]. */
/**
 * RAM feasibility — the chosen band LEVEL (0–4, 4 = best) for each of the six feasibility gates.
 * The survival rate is derived from the band via the RAM scoring matrix (`engine/ram-matrix.ts`),
 * so the score is computed from the matrix rather than a free-hand rate (schema v9). Legacy v≤8
 * rows carrying `*Feasibility` fields self-heal to the nearest band level at the parse boundary.
 */
export interface RamAssumptions {
  readonly technicalLevel: number;
  readonly regulatoryLevel: number;
  readonly dataRightsLevel: number;
  readonly securityLevel: number;
  readonly monetizationLevel: number;
  readonly distributionLevel: number;
}
export interface CustomerValueAssumptions {
  readonly costSavings: number;
  readonly revenueIncrease: number;
  readonly riskReduction: number;
  readonly timeSavings: number;
  readonly switchingCost: number;
  readonly integrationCost: number;
  readonly newOperatingCost: number;
  readonly annualPrice: number;
}
export interface UnitEconomicsAssumptions {
  readonly aiComputeCost: number;
  readonly apiCost: number;
  readonly dataCost: number;
  readonly cloudCost: number;
  readonly supportCost: number;
  readonly humanReviewCost: number;
}
export interface CacLtvAssumptions {
  readonly acquisitionSpend: number;
  readonly newCustomers: number;
  readonly customerLifetimeYears: number;
}
/** Competitive win-rate advantages, each in [0,1]. */
/**
 * Competitive win-rate fronts as qualitative-first band LEVELS (0–4, 4 = best), scored against
 * the win-rate matrix (`engine/win-matrix.ts`). The engine derives each front's win share from the
 * chosen band and multiplies the five. Legacy rows carried `*Advantage`/`switchingFeasibility`
 * rates; those self-heal to the nearest band at the parse boundary (schema v10).
 */
export interface WinRateAssumptions {
  readonly productLevel: number;
  readonly priceLevel: number;
  readonly distributionLevel: number;
  readonly trustLevel: number;
  readonly switchingLevel: number;
}
/** Defensibility factors, each scored 0–10. */
export interface DefensibilityAssumptions {
  readonly dataAdvantage: number;
  readonly workflowLockin: number;
  readonly switchingCost: number;
  readonly distributionControl: number;
  readonly brandTrust: number;
  readonly networkEffects: number;
  readonly regulatoryAdvantage: number;
}
/** Optional scenario multipliers; registry defaults apply when omitted. */
export interface ScenariosAssumptions {
  readonly conservativeMultiplier?: number;
  readonly aggressiveMultiplier?: number;
}
/**
 * §17 hybrid inputs: the 4 judgmental scores are LLM-supplied (0–100); the other
 * 4 (Customer ROI, Unit Economics, Defensibility, Feasibility) are DERIVED from
 * computed results. The 4 judgmental §18 booleans are LLM-supplied; the other 9
 * are computed from results/assumptions against registry thresholds.
 */
export interface ConfidenceAssumptions {
  readonly marketQualityScore: number; // 0–100
  readonly problemPainScore: number; // 0–100
  readonly adoptionScore: number; // 0–100
  readonly distributionScore: number; // 0–100
  readonly noClearBuyer: boolean;
  readonly noPainfulProblem: boolean;
  readonly accuracyRequirementTooHigh: boolean;
  readonly regulatoryBlocks: boolean;
}

// ── Per-stage Results (computed) ─────────────────────────────────────────────

export interface MarketTierResults {
  readonly customers: number;
  readonly revenue: number;
}
export interface TamResults extends MarketTierResults {
  readonly pricePerCustomer: number;
  /**
   * The evidence range around `revenue` in dollars (ADR 0059 §5), present only when BOTH
   * assumption ranges are. Display/provenance only — `revenue` (the point mid) remains the
   * sole scoring input; nothing downstream of TAM reads this field.
   */
  readonly range?: EvidenceRange;
}
export type SamResults = MarketTierResults;
export interface QsamResults extends MarketTierResults {
  readonly qualifyRate: number;
}
export interface AomResults extends MarketTierResults {
  readonly adoptionRate: number;
}
/**
 * IVS results: the weighted 0–10 score and the intuitive-value multiplier it applies to the
 * obtainable market downstream ("Intuitive-Adjusted AOM"). `multiplier` in [0.4, 1.0].
 */
export interface IvsResults {
  readonly score: number; // 0–10 weighted total
  readonly multiplier: number; // [0.4, 1.0] applied to AOM revenue downstream
}
export interface RamResults {
  readonly revenue: number;
  readonly riskFactor: number;
}
export interface CustomerValueResults {
  readonly valueCreated: number;
  readonly roi: number;
}
export interface UnitEconomicsResults {
  readonly grossProfit: number;
  readonly grossMargin: number; // ratio [..1]
}
export interface CacLtvResults {
  readonly cac: number;
  readonly ltv: number;
  readonly ltvCacRatio: number;
}
export interface WinRateResults {
  readonly winRate: number; // [0,1]
}
export interface DefensibilityResults {
  readonly total: number; // 0–70
  readonly ratio: number; // [0,1]
}
export interface ScenariosResults {
  readonly conservativeAom: number;
  readonly baseAom: number;
  readonly aggressiveAom: number;
}

/** Final verdict (the `confidence` stage's result). */
export type VerdictBand = 'strong-go' | 'diligence' | 'hold' | 'no-go';
/** All verdict bands as data (best → worst) — the single source for parsers (cf. STAGE_ORDER). */
export const VERDICT_BANDS = ['strong-go', 'diligence', 'hold', 'no-go'] as const satisfies readonly VerdictBand[];
export interface Verdict {
  readonly band: VerdictBand;
  readonly score: number; // 0–100 base investment score
  readonly fatalFlags: readonly string[]; // triggered §18 rule keys
  readonly upside: string;
  readonly bottleneck: string;
  readonly risk: string;
  readonly ventureScale: boolean;
  readonly reason: string;
}

/** Strongly-typed map of each stage's assumptions + results. */
export interface StageIO {
  tam: { assumptions: TamAssumptions; results: TamResults };
  sam: { assumptions: SamAssumptions; results: SamResults };
  qsam: { assumptions: QsamAssumptions; results: QsamResults };
  aom: { assumptions: AomAssumptions; results: AomResults };
  ivs: { assumptions: IvsAssumptions; results: IvsResults };
  ram: { assumptions: RamAssumptions; results: RamResults };
  customerValue: { assumptions: CustomerValueAssumptions; results: CustomerValueResults };
  unitEconomics: { assumptions: UnitEconomicsAssumptions; results: UnitEconomicsResults };
  cacLtv: { assumptions: CacLtvAssumptions; results: CacLtvResults };
  winRate: { assumptions: WinRateAssumptions; results: WinRateResults };
  defensibility: { assumptions: DefensibilityAssumptions; results: DefensibilityResults };
  scenarios: { assumptions: ScenariosAssumptions; results: ScenariosResults };
  confidence: { assumptions: ConfidenceAssumptions; results: Verdict };
}

/**
 * A persisted stage cell: its assumptions + computed results, plus optional
 * per-field `provenance` (where each sourced assumption came from). Provenance is
 * additive (schema v4, ADR 0034): cells written before it simply omit the field.
 */
export type StageCell<K extends StageKey> = StageIO[K] & {
  readonly provenance?: StageProvenance;
};

/** Partial per-stage map persisted on an Idea as it is evaluated. */
export type IdeaStages = { [K in StageKey]?: StageCell<K> };

/** A complete set of per-stage assumptions (the editable working set). */
export type AssumptionsBundle = { [K in StageKey]: StageIO[K]['assumptions'] };
